// ─────────────────────────────────────────────────────────────
// DeleteAccountModal
//
// Two-step confirmation:
//   1. Show what will happen (data loss, subscription cancel-at-period-end)
//   2. User types DELETE to enable the final button
//
// On confirm, calls the delete-account Edge Function. On success,
// signs the user out and redirects to the landing page with a toast.
// ─────────────────────────────────────────────────────────────

import { useState } from "react";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";
import { C } from "../../lib/colors";
import { Btn } from "../../components/ui/Btn";

export function DeleteAccountModal({ isOpen, onClose, profile, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const [error,      setError]        = useState(null);
  const [result,     setResult]       = useState(null);

  if (!isOpen) return null;

  const isConfirmed = confirmText.trim().toUpperCase() === "DELETE";

  const handleDelete = async () => {
    if (!isConfirmed || submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const res = await authFetch(
        `${SUPABASE_URL}/functions/v1/delete-account`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ confirmation_email: profile?.email }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Deletion failed");
      }
      setResult(data);
      // Don't auto-redirect — let the user read the confirmation at their
      // own pace. They click a button when ready to be signed out.
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  // ── Success screen ────────────────────────────────────────────────────────
  if (result?.success) {
    return (
      <ModalShell onClose={null /* must dismiss via button */}>
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>👋</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 10 }}>
            Account scheduled for deletion
          </h2>
          <p style={{ fontSize: 13, color: C.sub, lineHeight: 1.55, marginBottom: 14 }}>
            Your data will be permanently deleted in <strong>30 days</strong>.
          </p>
          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.55, marginBottom: 12 }}>
            Change your mind? Sign back in anytime in the next 30 days to restore your account.
          </p>
          {result.subscription_ends_at && (
            <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.55, marginBottom: 20 }}>
              Your subscription will remain active until{" "}
              <strong>
                {new Date(result.subscription_ends_at).toLocaleDateString("en-US", {
                  month: "long", day: "numeric", year: "numeric",
                })}
              </strong>.
            </p>
          )}
          <button
            onClick={() => onDeleted?.(result)}
            style={{
              marginTop: 14,
              padding: "12px 28px",
              background: C.blue,
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              minWidth: 200,
            }}
          >
            Got it — sign me out
          </button>
        </div>
      </ModalShell>
    );
  }

  // ── Confirmation screen ───────────────────────────────────────────────────
  return (
    <ModalShell onClose={submitting ? null : onClose}>
      <h2 style={{ fontSize: 20, fontWeight: 800, color: C.red, marginBottom: 8 }}>
        Delete account
      </h2>
      <p style={{ fontSize: 13, color: C.sub, lineHeight: 1.55, marginBottom: 16 }}>
        This will mark your account for deletion. Here's what happens:
      </p>

      <ul style={{ fontSize: 12, color: C.sub, lineHeight: 1.7, paddingLeft: 18, marginBottom: 18 }}>
        <li>You'll be signed out immediately.</li>
        <li>Your trades, accounts, strategies, and settings are kept for 30 days.</li>
        <li>If you sign back in within 30 days, you can restore your account.</li>
        <li>After 30 days, everything is permanently deleted.</li>
        <li>
          Your subscription will be set to cancel at the end of your current billing
          period — no refund for unused time, no further charges.
        </li>
      </ul>

      <div style={{
        background: C.red + "10",
        border: `1px solid ${C.red}40`,
        borderRadius: 6,
        padding: "10px 14px",
        fontSize: 12,
        color: C.red,
        marginBottom: 18,
        lineHeight: 1.5,
      }}>
        <strong>⚠ This is permanent after 30 days.</strong> Type <code style={{
          fontFamily: "monospace",
          background: "#1a1d2e",
          padding: "1px 5px",
          borderRadius: 3,
        }}>DELETE</code> below to confirm.
      </div>

      <input
        type="text"
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        disabled={submitting}
        placeholder="Type DELETE to confirm"
        style={{
          width: "100%",
          padding: "10px 12px",
          background: "#1a1d2e",
          border: `1px solid ${isConfirmed ? C.red : C.border}`,
          borderRadius: 6,
          color: C.text,
          fontSize: 14,
          fontFamily: "monospace",
          marginBottom: 16,
          outline: "none",
        }}
        autoFocus
      />

      {error && (
        <div style={{
          padding: "8px 12px",
          background: C.red + "15",
          border: `1px solid ${C.red}40`,
          borderRadius: 6,
          fontSize: 12,
          color: C.red,
          marginBottom: 14,
        }}>
          ⚠ {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn
          variant="ghost"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Btn>
        <button
          onClick={handleDelete}
          disabled={!isConfirmed || submitting}
          style={{
            padding: "10px 20px",
            background: isConfirmed && !submitting ? C.red : C.border,
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
            border: "none",
            borderRadius: 6,
            cursor: isConfirmed && !submitting ? "pointer" : "not-allowed",
            opacity: isConfirmed && !submitting ? 1 : 0.6,
          }}
        >
          {submitting ? "Deleting…" : "Delete account"}
        </button>
      </div>
    </ModalShell>
  );
}

// ── Modal shell ──────────────────────────────────────────────────────────────
function ModalShell({ children, onClose }) {
  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 16,
      }}
      onClick={onClose ? onClose : undefined}
    >
      <div
        style={{
          background: "#0f1322",
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          padding: 24,
          maxWidth: 480,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
